import React from "react";

import Input from "Components/Form/Input";
import Select from "Components/Form/Select";
import PanelContent from "Components/Panels/PanelContent";
import { unitsScheduleCombo } from "Requests/Units";
import { Field, useFormikContext } from "formik";
import { useQuery } from "react-query";
import { AttendancePeriodsContainer } from "../../style";

const dayOptions = [
  { id: 1, name: "Segunda-feira" },
  { id: 2, name: "Terça-feira" },
  { id: 3, name: "Quarta-feira" },
  { id: 4, name: "Quinta-feira" },
  { id: 5, name: "Sexta-feira" },
  { id: 6, name: "Sábado" },
];

function getDayLabel(dayId) {
  const dayOption = dayOptions.find((item) => item.id === Number(dayId));
  return dayOption?.name || "";
}

function toDateInputValue(value) {
  if (!value) return "";
  const normalizedValue = String(value).trim();
  if (!normalizedValue) return "";
  return normalizedValue.split(" ")[0].split("T")[0];
}

function toPeriodRow(
  value,
  index,
  onChange,
  options,
  isReadOnlyDay = false,
  fieldIdPrefix = "period"
) {
  return (
    <div className="row" key={`${fieldIdPrefix}-row-${index}`}>
      <div className="md-2">
        {index === 0 && <span className="text-gray text-semibold">Dia</span>}
        {isReadOnlyDay ? (
          <p style={{ marginTop: "0.75rem", fontWeight: 700 }}>
            {getDayLabel(value.day_id)}
          </p>
        ) : (
          <Field
            component={Select}
            id={`${fieldIdPrefix}_day_id_${index}`}
            value={value.day_id}
            options={dayOptions}
            optionName="name"
            placeholder="Selecione"
            onChange={(event) => onChange(index, "day_id", event.target.value)}
            readOnly={isReadOnlyDay}
          />
        )}
      </div>
      <div className="md-2">
        <Field
          component={Select}
          id={`${fieldIdPrefix}_start_id_${index}`}
          label={index === 0 ? "Início" : ""}
          value={value.start_id}
          options={options}
          optionName="hour"
          placeholder="Selecione"
          onChange={(event) => onChange(index, "start_id", event.target.value)}
        />
      </div>
      <div className="md-2">
        <Field
          component={Select}
          id={`${fieldIdPrefix}_end_id_${index}`}
          label={index === 0 ? "Fim" : ""}
          value={value.end_id}
          options={options}
          optionName="hour"
          placeholder="Selecione"
          onChange={(event) => onChange(index, "end_id", event.target.value)}
        />
      </div>
    </div>
  );
}

function BreakSection({ options }) {
  const { setFieldValue, values } = useFormikContext();

  const breakValues = values.break_periods || [];
  const value = breakValues[0] || {};

  const startDate = toDateInputValue(value.starts_at);
  const endDate = toDateInputValue(value.ends_at);

  function onUpdate(field, fieldValue) {
    setFieldValue("break_periods", [
      {
        ...(breakValues[0] || {}),
        [field]: fieldValue,
      },
    ]);
  }

  return (
    <div style={{ marginTop: "1rem" }}>
      <h4>Intervalo</h4>
      <div className="row">
        <div className="md-3">
          <Field
            component={Select}
            id="break_start_id"
            label="Início intervalo"
            value={value.start_id || ""}
            options={options}
            optionName="hour"
            placeholder="Selecione"
            onChange={(event) => onUpdate("start_id", event.target.value)}
          />
        </div>
        <div className="md-3">
          <Field
            component={Select}
            id="break_end_id"
            label="Fim intervalo"
            value={value.end_id || ""}
            options={options}
            optionName="hour"
            placeholder="Selecione"
            onChange={(event) => onUpdate("end_id", event.target.value)}
          />
        </div>
        <div className="md-3">
          <Field
            component={Input}
            id="break_starts_at"
            type="date"
            label="Intervalo inicia em"
            value={startDate}
            onChange={(event) => onUpdate("starts_at", event.target.value)}
          />
        </div>
        <div className="md-3">
          <Field
            component={Input}
            id="break_ends_at"
            type="date"
            label="Intervalo termina em"
            value={endDate}
            onChange={(event) => onUpdate("ends_at", event.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

function UnifiedAttendanceAndBreakRows({ options }) {
  const { values, setFieldValue } = useFormikContext();

  const unitValues = values.unit_periods || [];

  function updateUnitPeriod(index, field, fieldValue) {
    const nextValue = unitValues.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      return { ...item, [field]: fieldValue };
    });
    setFieldValue("unit_periods", nextValue);
  }

  return (
    <AttendancePeriodsContainer>
      {unitValues.map((unitRow, index) => {
        return (
          <div className="row" key={`unit-break-${unitRow.day_id || index}`}>
            <div className="md-2">
              {index === 0 && (
                <span className="text-gray text-semibold">Dia</span>
              )}
              <p style={{ marginTop: "0.75rem", fontWeight: 700 }}>
                {getDayLabel(unitRow.day_id)}
              </p>
            </div>
            <div className="md-2">
              <Field
                component={Select}
                id={`unit_start_id_${index}`}
                label={index === 0 ? "Início" : ""}
                value={unitRow.start_id}
                options={options}
                optionName="hour"
                placeholder="Selecione"
                onChange={(event) =>
                  updateUnitPeriod(index, "start_id", event.target.value)
                }
              />
            </div>
            <div className="md-2">
              <Field
                component={Select}
                id={`unit_end_id_${index}`}
                label={index === 0 ? "Fim" : ""}
                value={unitRow.end_id}
                options={options}
                optionName="hour"
                placeholder="Selecione"
                onChange={(event) =>
                  updateUnitPeriod(index, "end_id", event.target.value)
                }
              />
            </div>
          </div>
        );
      })}
      <BreakSection options={options} />
    </AttendancePeriodsContainer>
  );
}

function PeriodsSection({ title, fieldName, options }) {
  const { values, setFieldValue } = useFormikContext();

  const periodValues = values[fieldName] || [];

  function updateValue(index, field, value) {
    const nextValue = periodValues.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      return { ...item, [field]: value };
    });
    setFieldValue(fieldName, nextValue);
  }

  return (
    <PanelContent title={title}>
      {periodValues.map((value, index) => (
        <div key={`${fieldName}-${index}`}>
          {toPeriodRow(value, index, updateValue, options, true, fieldName)}
        </div>
      ))}
    </PanelContent>
  );
}

export function AttendancePeriods() {
  const { data: unitCombos } = useQuery({
    queryKey: ["unit-combos"],
    queryFn: unitsScheduleCombo,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return (
    <>
      <style>
        {`
          .attendance-periods-wrapper .field-holder {
            margin-bottom: 0;
          }
        `}
      </style>
      <PanelContent title="Horários de funcionamento">
        <div className="attendance-periods-wrapper">
          <UnifiedAttendanceAndBreakRows options={unitCombos?.hours ?? []} />
        </div>
      </PanelContent>

      <PeriodsSection
        title="Dias de atendimento para coleta"
        fieldName="collect_periods"
        options={unitCombos?.hours ?? []}
      />

      <PeriodsSection
        title="Dias de atendimento entrega de resultado"
        fieldName="report_periods"
        options={unitCombos?.hours ?? []}
      />
    </>
  );
}
